import { z } from 'zod';
import { Dateish, OptionalEmail, PaginationQuerySchema, intList } from './common';
import { DeskOriginSchema } from './desks';
import { TICKET_SITUATIONS, TicketSituationSchema } from './situations';
import { SgrSheetDriftSchema } from './sgr-routine';
import { SheetFromSgrViewSchema } from './sgr-vehicles';
import { TicketSheetInputSchema, TicketSheetSchema } from './ticket-sheet';
import { CommentAuthorKindSchema, TicketListViewSchema, WorkScopeSchema } from './work-views';

export const TicketRefSchema = z.object({ id: z.number().int(), name: z.string() });

export const CreatedViaSchema = z.enum([
  'web',
  'api',
  'import',
  'email',
  'portal',
  // v5: atividade agendada (scheduler) e formulário externo público
  'scheduled',
  'form',
]);
export type CreatedVia = z.infer<typeof CreatedViaSchema>;

/** Rótulos pt-BR da origem do ticket (padrão CHAT_ORIGIN_LABELS de ./chats). */
export const ORIGIN_LABELS: Record<CreatedVia, string> = {
  web: 'Web',
  api: 'API',
  import: 'Importação',
  email: 'E-mail',
  portal: 'Portal',
  scheduled: 'Agendado',
  form: 'Formulário',
};

/**
 * POST /inbound/email (v4) — payload normalizado, agnóstico de provedor
 * (SES/Mailgun/Postmark mapeiam para isto no gateway). `to` decide a mesa
 * pelo local-part; `subject` com `#<número>` encadeia como comentário.
 */
export const InboundEmailPayloadSchema = z.object({
  to: z.string().min(1),
  from: z.string().min(1),
  fromName: z.string().optional(),
  subject: z.string(),
  text: z.string().optional(),
  html: z.string().optional(),
});
export type InboundEmailPayload = z.infer<typeof InboundEmailPayloadSchema>;

/**
 * Mesa do ticket na lista e no detalhe, com o dono DA MESA (spec 011). Desde a
 * spec 024 ela não decide mais se o TICKET é espelhado — um ticket fora do
 * espelho vive numa mesa `tiflux` e é editável; quem decide é `mirrored`.
 */
export const TicketDeskRefSchema = TicketRefSchema.extend({ origin: DeskOriginSchema });
export type TicketDeskRef = z.infer<typeof TicketDeskRefSchema>;

/**
 * Por que o ticket saiu do espelho do TiFlux (spec 024): o cliente dele foi
 * desligado (todos os tickets de então), o ticket foi desligado sozinho, ou
 * ele nasceu no ERP pela API numa mesa espelhada (fase 2).
 *
 * Spec 026: `arrived_operated` — o ticket de um cliente operado no ERP que
 * CHEGOU pelo TiFlux e foi adotado logo depois do primeiro detalhe completo
 * (ficha, solicitante, comentários, apontamentos). Sem autor: não foi uma
 * pessoa, e a auditoria o distingue do desligamento manual.
 */
export const MIRROR_DETACH_REASONS = ['client_detached', 'ticket_detached', 'api', 'arrived_operated'] as const;
export const MirrorDetachReasonSchema = z.enum(MIRROR_DETACH_REASONS);
export type MirrorDetachReason = z.infer<typeof MirrorDetachReasonSchema>;

export const MIRROR_DETACH_REASON_LABELS: Record<MirrorDetachReason, string> = {
  client_detached: 'cliente operado no ERP',
  ticket_detached: 'ticket desligado do TiFlux',
  api: 'criado pela API',
  arrived_operated: 'chegou já operado no ERP',
};

export const TicketListItemSchema = z.object({
  ticketNumber: z.number().int(),
  title: z.string(),
  client: TicketRefSchema,
  desk: TicketDeskRefSchema,
  /**
   * Espelhado do TiFlux (spec 024, FR-001): mesa `tiflux` E sem a marca "fora
   * do espelho". É com ele — nunca com `desk.origin` — que a tela mostra o selo
   * e esconde a edição; a API recusaria a escrita com 409 ticket_mirrored.
   */
  mirrored: z.boolean(),
  stage: TicketRefSchema,
  status: TicketRefSchema.extend({ kind: z.enum(['open', 'closed', 'canceled', 'custom']) }),
  priority: TicketRefSchema.nullable(),
  responsible: TicketRefSchema.nullable(),
  /** grupo de tickets (v11 §3); null = sem grupo */
  group: TicketRefSchema.nullable(),
  requestorName: z.string().nullable(),
  createdVia: CreatedViaSchema,
  // ── SLA (v3) — campos crus; o front deriva o estado com ./business-hours ──
  catalogItemId: z.number().int().nullable(),
  attendDueAt: Dateish.nullable(),
  solveDueAt: Dateish.nullable(),
  attendedAt: Dateish.nullable(),
  /** não-nulo = SLA pausado agora */
  slaPausedAt: Dateish.nullable(),
  /** minutos ÚTEIS acumulados de pausas já retomadas */
  slaPausedMinutes: z.number().int(),
  stageEnteredAt: Dateish,
  closedAt: Dateish.nullable(),
  canceledAt: Dateish.nullable(),
  createdAt: Dateish,
  updatedAt: Dateish,
  /**
   * Situação EFETIVA (spec 017, FR-001): fechado → done, cancelado → canceled,
   * senão a da etapa. O rótulo vem do perfil do tenant (./profiles).
   */
  situation: TicketSituationSchema,
  /**
   * Passou do prazo da etapa (`max_time_minutes`, minutos corridos desde
   * `stageEnteredAt` — FR-008), no instante da resposta. Ticket sem SLA, como o
   * espelhado do TiFlux, também tem funil: é esse o risco que isto mede.
   */
  stuckInStage: z.boolean(),
  /**
   * Desde quando o cliente espera resposta (spec 018): a mensagem PÚBLICA mais
   * antiga do cliente/contato ainda sem resposta pública da equipe — comentário
   * do portal/e-mail ou WhatsApp de conversa vinculada. null = ninguém espera.
   * Nota interna não é resposta; resposta pública da equipe limpa.
   */
  awaitingReplySince: Dateish.nullable(),
  /**
   * "Atualização" da lista (spec 018): o mais recente entre a última mudança do
   * ticket (`updatedAt`) e a última conversa nele (comentário ou mensagem de
   * conversa vinculada) — comentário não mexia em `updatedAt`, e a lista
   * ordenada por atualização escondia o ticket em que o cliente acabou de falar.
   */
  activityAt: Dateish,
  /**
   * Identificador do recurso do atendimento no perfil do tenant (017): a placa
   * extraída da ficha (rastreamento) ou o equipamento cadastrado (genérico);
   * null quando não há ou o perfil não trabalha com recurso.
   */
  resourceLabel: z.string().nullable(),
});
export type TicketListItem = z.infer<typeof TicketListItemSchema>;

export const TicketDetailSchema = TicketListItemSchema.extend({
  description: z.string().nullable(),
  requestorEmail: z.string().nullable(),
  requestorPhone: z.string().nullable(),
  followers: z.string().nullable(),
  equipment: TicketRefSchema.nullable(),
  /**
   * Técnico responsável pelo atendimento em campo (pedido da PoolTrack,
   * 02/10): um do cadastro de técnicos — o sincronizado da origem ou o
   * cadastrado à mão. Não é usuário do ERP; quem atende no sistema continua
   * sendo `responsible`.
   */
  technician: TicketRefSchema.nullable(),
  workedMinutes: z.number().int(),
  runningTimerUserIds: z.array(z.number().int()),
  /**
   * Metas resolvidas (item do catálogo > prioridade, ./sla) em minutos úteis —
   * o front exibe "Meta" direto daqui, sem baixar árvores de catálogo.
   */
  attendGoalMinutes: z.number().int().nullable(),
  solveGoalMinutes: z.number().int().nullable(),
  /** fora do espelho do TiFlux desde (spec 024); null = nunca saiu */
  mirrorDetachedAt: Dateish.nullable(),
  mirrorDetachedReason: MirrorDetachReasonSchema.nullable(),
  /**
   * A ficha do associado (spec 031): SÓ o que uma pessoa escreveu, slot a slot
   * — null segue a origem (descrição, título, formulário), '' é "apagado". O
   * efetivo sai de resolveTicketSheet, que roda aqui no navegador com os
   * campos acima.
   */
  sheet: TicketSheetSchema,
  /** a última edição da ficha (quem e quando); null = ninguém editou */
  sheetEditedAt: Dateish.nullable(),
  sheetEditedBy: TicketRefSchema.nullable(),
  /**
   * A procedência da ficha escolhida do SGR (spec 032, §2.5): a FOTO do que o
   * SGR dizia quando alguém escolheu o veículo. Não é camada da precedência — o
   * efetivo continua em `sheet`. O cartão diz "do SGR" quando o slot ainda é
   * igual à foto, e "Voltar ao do SGR" repõe o valor dela. null = a ficha não
   * veio do SGR (ou a API é anterior à spec 032).
   */
  sheetFromSgr: SheetFromSgrViewSchema.nullable(),
  /** o associado do veículo ligado (spec 032, "Ver associado"); null = o recurso não é do SGR */
  endCustomer: TicketRefSchema.nullable(),
  /**
   * O SGR mudou desde a foto? (spec 035, D5) O registro ATUAL do veículo ligado
   * (a última leitura, que a rotina noturna refaz) contra `sheetFromSgr`, slot a
   * slot. null = não há com o que comparar (sem foto, recurso de outro veículo,
   * veículo ainda não relido); ausente = API anterior à spec 035.
   */
  sgrDrift: SgrSheetDriftSchema.nullable().optional(),
  /**
   * O motivo de fechamento (spec 054) e a observação de quem fechou. null =
   * aberto, cancelado, ou fechado sem motivo (mesa sem motivo, TiFlux, ou antes
   * da spec 054). Ausente = API anterior à spec 054.
   */
  closeReason: TicketRefSchema.nullable().optional(),
  closeNote: z.string().nullable().optional(),
});
export type TicketDetail = z.infer<typeof TicketDetailSchema>;

// ── Fechar com motivo (spec 054) ────────────────────────────────────────────
export const TICKET_CLOSE_NOTE_MAX = 2000;

/**
 * POST /tickets/:n/close. Sem corpo continua valendo na mesa sem motivo; na
 * mesa com motivo ativo, `reasonId` é obrigatório (`close_reason_required`) e
 * tem de ser um motivo ATIVO da mesa do ticket (`close_reason_invalid`).
 * Observação vazia vale como nenhuma.
 */
export const TicketCloseBodySchema = z
  .object({
    reasonId: z.number().int().positive().optional(),
    note: z.string().trim().max(TICKET_CLOSE_NOTE_MAX).optional(),
  })
  .strict();
export type TicketCloseBody = z.infer<typeof TicketCloseBodySchema>;

/** GET /tickets/:n/close-reasons: o que o diálogo de fechamento oferece */
export const TicketCloseOptionsSchema = z.object({
  /** a mesa tem motivo ativo: fechar sem escolher é recusado */
  required: z.boolean(),
  /** os motivos ATIVOS da mesa do ticket, na ordem da mesa */
  reasons: z.array(TicketRefSchema),
});
export type TicketCloseOptions = z.infer<typeof TicketCloseOptionsSchema>;

/** "waiting_client,waiting_contact" → situações efetivas; lixo é 422, não filtro vazio. */
const situationList = z
  .string()
  .transform((s) =>
    s
      .split(',')
      .map((x) => x.trim())
      .filter((x) => x !== '')
  )
  .pipe(z.array(TicketSituationSchema).min(1).max(TICKET_SITUATIONS.length));

export const TicketListQuerySchema = PaginationQuerySchema.extend({
  /**
   * Visão de trabalho (spec 018) — o mesmo predicado dos indicadores do Hoje.
   * Ausente = a lista de sempre. As visões de trabalho olham só os abertos.
   */
  view: TicketListViewSchema.optional(),
  /** escopo do Hoje: `mine` = meus OU sem responsável (spec 018) */
  scope: WorkScopeSchema.optional(),
  /** situação efetiva (spec 017) — marcador de filtro da lista simplificada */
  situations: situationList.optional(),
  desk_ids: intList.optional(),
  stage_ids: intList.optional(),
  client_ids: intList.optional(),
  responsible_ids: intList.optional(),
  /** grupo de tickets (v11 §3) */
  group_id: z.coerce.number().int().positive().optional(),
  // 'all' by default — hiding closed tickets was one of the Tiflux pains
  status: z.enum(['open', 'closed', 'canceled', 'all']).default('all'),
  /** título, nº, cliente e solicitante sem acento; placa e telefone em
   *  qualquer grafia (spec 012, ./ticket-search) */
  search: z.string().optional(),
  created_start: z.string().optional(),
  created_end: z.string().optional(),
  /**
   * `activity` (spec 018) ordena pela "Atualização" da lista (activityAt);
   * `awaiting` pela espera do cliente, mais antiga primeiro quando asc.
   */
  sort: z.enum(['created_at', 'updated_at', 'activity', 'awaiting']).default('updated_at'),
  order: z.enum(['asc', 'desc']).default('desc'),
});
export type TicketListQuery = z.infer<typeof TicketListQuerySchema>;

export const TicketCreateBodySchema = z.object({
  title: z.string().min(1),
  description: z.string().nullish(),
  clientId: z.number().int().positive(),
  deskId: z.number().int().positive(),
  stageId: z.number().int().positive().optional(),
  priorityId: z.number().int().positive().nullish(),
  responsibleId: z.number().int().positive().nullish(),
  requestorName: z.string().nullish(),
  /** formato validado: endereço malformado só é descoberto virando bounce */
  requestorEmail: OptionalEmail,
  requestorPhone: z.string().nullish(),
  followers: z.string().nullish(),
  equipmentId: z.number().int().positive().nullish(),
  /** item do catálogo (validado contra os catálogos vinculados à mesa) */
  catalogItemId: z.number().int().positive().nullish(),
  /**
   * A ficha do associado digitada no "Novo ticket" (spec 031). O formulário
   * manda `null` no campo vazio (segue o resto da ordem), nunca `''`.
   */
  sheet: TicketSheetInputSchema.optional(),
  /**
   * O RECIBO assinado que o `/sgr/vehicles/:id/pick` devolveu (spec 032, §4): o
   * servidor grava `sheet_from_sgr` a partir DELE, e não do que o navegador diz.
   * Recibo inválido ou vencido é descartado, e a ficha é gravada assim mesmo,
   * como editada.
   */
  sheetFromSgr: z.string().max(16384).optional(),
});
export type TicketCreateBody = z.infer<typeof TicketCreateBodySchema>;

/** PATCH — includes re-parent (clientId) and desk move (deskId + stageId/statusId of the target desk). */
export const TicketPatchBodySchema = z
  .object({
    title: z.string().min(1),
    description: z.string().nullable(),
    clientId: z.number().int().positive(),
    deskId: z.number().int().positive(),
    stageId: z.number().int().positive(),
    statusId: z.number().int().positive(),
    priorityId: z.number().int().positive().nullable(),
    responsibleId: z.number().int().positive().nullable(),
    requestorName: z.string().nullable(),
    /** idem POST: `''` (campo limpo no SPA) vira null; lixo é 422 */
    requestorEmail: OptionalEmail,
    requestorPhone: z.string().nullable(),
    followers: z.string().nullable(),
    equipmentId: z.number().int().positive().nullable(),
    /** item do catálogo (validado contra os catálogos vinculados à mesa) */
    catalogItemId: z.number().int().positive().nullable(),
    /** grupo de tickets (v11 §3): existente E ativo no tenant, ou null */
    groupId: z.number().int().positive().nullable(),
    /**
     * técnico responsável (cadastro de técnicos do tenant, ativo ou não), ou
     * null para limpar. Corrigível também em ticket fechado, como o solicitante.
     */
    technicianId: z.number().int().positive().nullable(),
    /**
     * A ficha do associado (spec 031): só os slots presentes mudam. `null`
     * volta o slot à origem ("voltar ao da descrição"), `''` apaga. Correção
     * de registro, como o solicitante: vale em ticket fechado (com
     * tickets.edit_closed); no espelhado, 409 ticket_mirrored.
     */
    sheet: TicketSheetInputSchema,
    /** o recibo do `/pick` (spec 032) — escolher o veículo no campo Recurso de um ticket existente */
    sheetFromSgr: z.string().max(16384),
  })
  .partial()
  .strict();
export type TicketPatchBody = z.infer<typeof TicketPatchBodySchema>;

// ── Grupos de tickets (v11 §3): CRUD /ticket-groups ─────────────────────────
export const TICKET_GROUP_NAME_MAX = 120;

export const TicketGroupSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  active: z.boolean(),
  createdAt: Dateish,
});
export type TicketGroup = z.infer<typeof TicketGroupSchema>;

/** POST / e PUT /:id — Nome* + Ativo (dialog fiel ao TiFlux, captura 82) */
export const TicketGroupBodySchema = z.object({
  name: z.string().trim().min(1).max(TICKET_GROUP_NAME_MAX),
  active: z.boolean().default(true),
});
export type TicketGroupBody = z.infer<typeof TicketGroupBodySchema>;

/**
 * GET /ticket-groups?active=1 — só grupos ATIVOS (selectors do detalhe/lista
 * de tickets); sem o parâmetro, todos (tela de configuração). A regra vive na
 * API para todos os consumidores dividirem UMA semântica (v11 review §9).
 */
export const TicketGroupsListQuerySchema = z.object({
  active: z.literal('1').optional(),
});
export type TicketGroupsListQuery = z.infer<typeof TicketGroupsListQuerySchema>;

// ── Pré-tickets (v5): triagem de e-mails de mesa com inbound_mode=preticket ─
export const PreticketStatusSchema = z.enum(['pending', 'accepted', 'discarded']);
export type PreticketStatus = z.infer<typeof PreticketStatusSchema>;

export const PreticketSchema = z.object({
  id: z.number().int(),
  desk: TicketRefSchema,
  fromEmail: z.string(),
  fromName: z.string().nullable(),
  subject: z.string(),
  body: z.string(),
  status: PreticketStatusSchema,
  /** ticket criado no accept; null enquanto pending ou quando descartado */
  ticketNumber: z.number().int().nullable(),
  decidedBy: z.number().int().nullable(),
  decidedAt: Dateish.nullable(),
  createdAt: Dateish,
});
export type Preticket = z.infer<typeof PreticketSchema>;

/** GET /pretickets — default pending; total via X-Total-Items. */
export const PreticketListQuerySchema = PaginationQuerySchema.extend({
  status: PreticketStatusSchema.default('pending'),
});
export type PreticketListQuery = z.infer<typeof PreticketListQuerySchema>;

/**
 * POST /pretickets/:id/accept — o staff ESCOLHE o cliente; título/descrição
 * default do pré-ticket (subject/body) quando omitidos.
 */
export const PreticketAcceptBodySchema = z.object({
  clientId: z.number().int().positive(),
  title: z.string().min(1).optional(),
  description: z.string().nullish(),
  priorityId: z.number().int().positive().nullish(),
});
export type PreticketAcceptBody = z.infer<typeof PreticketAcceptBodySchema>;

// ── Formulário externo (v5): rotas públicas /public/form/:token ─────────────
export const PublicFormViewSchema = z.object({
  deskName: z.string(),
});
export type PublicFormView = z.infer<typeof PublicFormViewSchema>;

export const PublicFormSubmitSchema = z.object({
  name: z.string().min(1),
  email: z.string().email(),
  title: z.string().min(1),
  description: z.string().min(1),
});
export type PublicFormSubmit = z.infer<typeof PublicFormSubmitSchema>;

// ── Comments ────────────────────────────────────────────────────────────────
export const TicketCommentSchema = z.object({
  id: z.number().int(),
  ticketNumber: z.number().int(),
  userId: z.number().int().nullable(),
  /**
   * Quem escreveu (spec 018): equipe, cliente/contato (portal, resposta por
   * e-mail) ou sistema (automação, espelho do TiFlux). É o que faz uma resposta
   * pública da equipe limpar o "precisa de resposta" e a do cliente acendê-lo.
   */
  authorKind: CommentAuthorKindSchema,
  authorName: z.string(),
  body: z.string(),
  internal: z.boolean(),
  createdAt: Dateish,
  /**
   * Menções da comunicação interna (spec 037, PR C): quem foi mencionado e o
   * nome com que aparece no texto (`@Nome`). Ausente em API antiga; só existe
   * em comentário interno, que o portal nunca recebe.
   */
  mentions: z.array(z.object({ userId: z.number().int(), name: z.string() })).optional(),
});
export type TicketComment = z.infer<typeof TicketCommentSchema>;

/** quantas pessoas um comentário menciona, no máximo */
export const COMMENT_MENTIONS_MAX = 20;

export const TicketCommentBodySchema = z.object({
  /**
   * spec 068 (D4): pode vir vazio quando há anexo (o print colado). Quem decide é
   * o caso de uso: sem texto e sem anexo, 422 `comment_empty`; sem texto com
   * e-mail, 422 `comment_email_needs_text`.
   */
  body: z.string(),
  internal: z.boolean().default(false),
  /** envia o comentário por e-mail ao solicitante (só público + requestorEmail) */
  sendEmail: z.boolean().optional(),
  /** anexos já enviados (POST /tickets/:n/files) a vincular a este comentário */
  fileIds: z.array(z.number().int().positive()).optional(),
  /**
   * spec 037 (PR C): ids da EQUIPE mencionada com `@Nome` no texto — só em
   * comunicação interna. A API confere cada um (equipe ativa do tenant, que
   * abre este ticket, com o `@Nome` no texto) e recusa o comentário inteiro se
   * algum não passar: nada é mencionado em silêncio, nada some em silêncio.
   */
  mentions: z.array(z.number().int().positive()).max(COMMENT_MENTIONS_MAX).optional(),
});
export type TicketCommentBody = z.infer<typeof TicketCommentBodySchema>;

// ── Anexos (v3) ─────────────────────────────────────────────────────────────
export const TicketFileSchema = z.object({
  id: z.number().int(),
  ticketNumber: z.number().int(),
  /** anexo vinculado a um comentário; null = anexo solto do ticket */
  commentId: z.number().int().nullable(),
  fileName: z.string(),
  size: z.number().int(),
  contentType: z.string(),
  uploadedBy: z.number().int().nullable(),
  createdAt: Dateish,
});
export type TicketFile = z.infer<typeof TicketFileSchema>;

// ── Appointments (apontamentos) ─────────────────────────────────────────────
export const AppointmentSchema = z.object({
  id: z.number().int(),
  ticketNumber: z.number().int(),
  user: TicketRefSchema,
  startedAt: Dateish,
  /** null = running timer */
  endedAt: Dateish.nullable(),
  description: z.string().nullable(),
  createdAt: Dateish,
  /**
   * Veio do TiFlux (tem vínculo no espelho). Só a lista do ticket (GET
   * /tickets/:n/appointments) e a resposta do PATCH o informam; ausente = não
   * informado. Excluir sempre vale — o ciclo respeita o apagado no ERP.
   */
  fromTiflux: z.boolean().optional(),
  /**
   * Veio do TiFlux E o ticket NÃO é de cliente operado no ERP: editar é
   * recusado (409 appointment_from_tiflux). No ticket de cliente operado o
   * vinculado se edita, e a edição vence o TiFlux (ADR-013, 03/10): o ciclo e o
   * "Puxar do TiFlux" não o regravam mais. Mesma presença de `fromTiflux`.
   */
  tifluxLocked: z.boolean().optional(),
  /**
   * Editado no ERP — quando e por quem (a última edição). Num apontamento que
   * veio do TiFlux é a marca de "o ERP vence": o ciclo não o regrava, e o que
   * mudar lá depois vira pendência. Mesma presença de `fromTiflux`.
   */
  erpEdited: z
    .object({ at: Dateish, by: z.object({ id: z.number().int(), name: z.string() }).nullable() })
    .nullable()
    .optional(),
});
export type Appointment = z.infer<typeof AppointmentSchema>;

export const AppointmentCreateBodySchema = z.object({
  startedAt: z.string(),
  endedAt: z.string(),
  description: z.string().nullish(),
});
export type AppointmentCreateBody = z.infer<typeof AppointmentCreateBodySchema>;

/**
 * Quem lançou o apontamento quando não foi uma pessoa nem o espelho do TiFlux
 * (`ticket_appointments.origin`; NULL = pessoa ou TiFlux).
 * `dispatch_first_contact`: o 1º contato com o contato, lançado pelo envio do
 * disparo do WhatsApp (pedido da PoolTrack, 05/10) — um por ticket.
 * `commitment_done`: o "Marcar como feito" de um agendamento da agenda (spec
 * 039, adendo 4) — em nome de quem marcou, "Serviço concluído", um por "feito".
 */
export const APPOINTMENT_ORIGINS = ['dispatch_first_contact', 'commitment_done'] as const;
export type AppointmentOrigin = (typeof APPOINTMENT_ORIGINS)[number];
export const FIRST_CONTACT_APPOINTMENT_ORIGIN: AppointmentOrigin = 'dispatch_first_contact';
export const COMMITMENT_DONE_APPOINTMENT_ORIGIN: AppointmentOrigin = 'commitment_done';

/** as origens que são REGISTRO, e não trabalho: as únicas com duração zero */
const ZERO_DURATION_ORIGINS: ReadonlySet<AppointmentOrigin> = new Set([
  FIRST_CONTACT_APPOINTMENT_ORIGIN,
  COMMITMENT_DONE_APPOINTMENT_ORIGIN,
]);

export type AppointmentPeriodError = 'invalid_date' | 'invalid_period';

const appointmentInstant = (v: Date | string) => new Date(v).getTime();

/**
 * O período de um apontamento finalizado — a regra ÚNICA da criação (a API e a
 * tela): as duas pontas legíveis (`invalid_date`) e o fim estritamente depois
 * do início (`invalid_period`). Não há teto de duração nem recusa de futuro.
 *
 * Duração ZERO só onde ela é legítima, e por isso só com a origem dita:
 * - o 1º contato que o disparo lança (decisão do operador, 05/10): é registro,
 *   não trabalho — não soma hora no atendente, na folha, no extrato nem no
 *   consumo do contrato por horas;
 * - o "feito" de um agendamento da agenda (spec 039, adendo 4): na PoolTrack o
 *   apontamento marca o que foi feito, e a janela do agendamento (a tarde são
 *   5 h) inflaria as horas apontadas e a cobrança;
 * - (na edição) o período que a edição NÃO muda — appointmentEditPeriodError.
 * A pessoa lançando à mão (origin null) nunca cria duração zero.
 */
export function appointmentPeriodError(
  startedAt: Date | string,
  endedAt: Date | string,
  origin: AppointmentOrigin | null = null
): AppointmentPeriodError | null {
  const start = appointmentInstant(startedAt);
  const end = appointmentInstant(endedAt);
  if (Number.isNaN(start) || Number.isNaN(end)) return 'invalid_date';
  if (end > start) return null;
  if (end === start && origin !== null && ZERO_DURATION_ORIGINS.has(origin)) return null;
  return 'invalid_period';
}

/**
 * A edição: o período que vai valer (o que veio + o que ficou) passa pela regra
 * da criação — MAS a edição não rejulga um período que ela não muda. Corrigir
 * só a descrição de um apontamento de duração zero (o 1º contato do disparo)
 * tem de passar; mexer no início ou no fim volta à regra da criação inteira, e
 * a pessoa não chega a duração zero por edição.
 */
export function appointmentEditPeriodError(
  stored: { startedAt: Date | string; endedAt: Date | string },
  next: { startedAt: Date | string; endedAt: Date | string }
): AppointmentPeriodError | null {
  const start = appointmentInstant(next.startedAt);
  const end = appointmentInstant(next.endedAt);
  if (Number.isNaN(start) || Number.isNaN(end)) return 'invalid_date';
  const unchanged =
    start === appointmentInstant(stored.startedAt) && end === appointmentInstant(stored.endedAt);
  return unchanged ? null : appointmentPeriodError(next.startedAt, next.endedAt);
}

/**
 * PATCH /tickets/:n/appointments/:id (pedido da PoolTrack, 02/10): os MESMOS
 * campos da criação, todos opcionais — o que não vem fica como está. Início e
 * fim são instantes ISO (o formulário converte do horário local, como na
 * criação); `description: null` limpa. O dono não muda: `.strict()` recusa
 * qualquer outro campo.
 */
export const AppointmentPatchBodySchema = z
  .object({
    startedAt: z.string().optional(),
    endedAt: z.string().optional(),
    description: z.string().nullish(),
  })
  .strict();
export type AppointmentPatchBody = z.infer<typeof AppointmentPatchBodySchema>;

/** campos que a edição muda — a ordem é a das `changes` do evento appointment_edited */
export const APPOINTMENT_EDIT_FIELDS = ['startedAt', 'endedAt', 'description'] as const;
export type AppointmentEditField = (typeof APPOINTMENT_EDIT_FIELDS)[number];

export const AppointmentStopBodySchema = z.object({
  description: z.string().nullish(),
});

export const GlobalAppointmentsQuerySchema = PaginationQuerySchema.extend({
  start_date: z.string(),
  end_date: z.string(),
  user_ids: intList.optional(),
  desk_ids: intList.optional(),
});
export type GlobalAppointmentsQuery = z.infer<typeof GlobalAppointmentsQuerySchema>;

export const GlobalAppointmentSchema = AppointmentSchema.extend({
  desk: TicketRefSchema,
  ticketTitle: z.string(),
});
export type GlobalAppointment = z.infer<typeof GlobalAppointmentSchema>;

/**
 * GET /appointments/totals — os MESMOS filtros do GET /appointments, sem a
 * paginação: a folha de horas pagina a lista e soma o período inteiro aqui.
 */
export const GlobalAppointmentsTotalsQuerySchema = GlobalAppointmentsQuerySchema.omit({
  limit: true,
  offset: true,
});
export type GlobalAppointmentsTotalsQuery = z.infer<typeof GlobalAppointmentsTotalsQuerySchema>;

const AppointmentsTotalsFields = {
  /** minutos dos FINALIZADOS, cada apontamento arredondado ao minuto como na tela */
  totalMinutes: z.number().int(),
  /** apontamentos no filtro, finalizados e em andamento */
  count: z.number().int(),
  /** em andamento (sem fim) — ficam fora de totalMinutes */
  runningCount: z.number().int(),
};

export const GlobalAppointmentsTotalsSchema = z.object({
  ...AppointmentsTotalsFields,
  /** por pessoa, ordenado por nome */
  byUser: z.array(z.object({ user: TicketRefSchema, ...AppointmentsTotalsFields })),
});
export type GlobalAppointmentsTotals = z.infer<typeof GlobalAppointmentsTotalsSchema>;

// ── Events (timeline) ───────────────────────────────────────────────────────
export const TicketEventTypeSchema = z.enum([
  'created',
  'updated',
  'stage_changed',
  'status_changed',
  'priority_changed',
  'responsible_changed',
  'reparented',
  'desk_moved',
  'closed',
  'canceled',
  'reopened',
  'comment_added',
  'appointment_added',
  'appointment_deleted',
  // pedido da PoolTrack (02/10): apontamento editado. Payload {appointmentId,
  // ownerUserId, changes: [{field, from, to}] (só os campos que mudaram, na
  // ordem de APPOINTMENT_EDIT_FIELDS; instantes em ISO), minutes: {from, to}
  // quando início ou fim mudou}. Não é notificável, como o appointment_deleted.
  'appointment_edited',
  'timer_started',
  'timer_stopped',
  'sla_paused',
  'sla_resumed',
  'file_added',
  'file_deleted',
  // spec 016: OS gerada a partir do ticket (payload {serviceOrderId, number, type})
  'service_order_created',
  // spec 014: disparo por ticket. Payload {from, to} (nomes de estágio) quando
  // o estágio mudou; + chatId (enviado/respondido) ou reason (falhou). Não são
  // notificáveis (fora de TICKET_EVENT_TO_NOTIFICATION_KEY): 80 disparos por
  // dia não podem acender 80 sinos — a resposta já acende o do chat.
  'dispatch_sent',
  'dispatch_failed',
  'dispatch_replied',
  // spec 022: a nota do bot (comentário interno, autor "Bot"). Payload
  // {bot: {keyId, keyName}, commentId, internal}. Não é notificável (fora de
  // TICKET_EVENT_TO_NOTIFICATION_KEY) nem entra no feed recente: são várias por
  // conversa do bot, e cada uma tomaria uma das 20 vagas do sino.
  'bot_note_added',
  // spec 026: "Puxado do TiFlux por <admin>" — um por ticket aplicado, com o
  // que mudou. Payload {source:'tiflux_pull', tifluxTicketNumber, applied:
  // [{field, from, to}], kept: [{field, tiflux, erp}], comments, appointments:
  // {inserted, updated}}. As mudanças de campo ganham os eventos de sempre
  // (pelos casos de uso normais), com `source: 'tiflux_pull'` no payload.
  'tiflux_pulled',
  // pedido da PoolTrack (02/10): técnico responsável do ticket. Payload {from,
  // to} com os NOMES (o técnico pode ser renomeado pela sincronização depois;
  // a linha do tempo guarda o que estava escrito na hora). Não é notificável:
  // técnico não é usuário do ERP e não tem sino.
  'technician_changed',
  // spec 043: o espelho encerrou o ticket porque ele já tinha acabado no TiFlux.
  // Payload {source: 'tiflux_mirror', tifluxTicketNumber, outcome: 'closed' |
  // 'canceled' | 'deleted'} e, no 'deleted' (404 lá), reason 'tiflux_not_found'.
  // Sem autor. NÃO é o `closed`/`canceled` de propósito: aqueles são o fluxo do
  // ERP (automação, CSAT, aviso, relatório de quem fechou), e este não dispara
  // nada — o ticket já tinha acabado lá. Fora do sino e do feed recente.
  'tiflux_closed',
]);
export type TicketEventType = z.infer<typeof TicketEventTypeSchema>;

/**
 * Tipos que ficam FORA do feed recente (GET /tickets/events/recent). O painel e
 * o sino leem as MESMAS 20 linhas: um tipo que chega várias vezes por conversa
 * do bot empurraria as notificações de verdade para fora da janela. O resumo do
 * "Puxar do TiFlux" (spec 026) também: um por ticket aplicado, e as mudanças de
 * campo já entram no feed pelos eventos de sempre.
 */
export const RECENT_FEED_HIDDEN_EVENT_TYPES = [
  'bot_note_added',
  'tiflux_pulled',
  // spec 043: a dívida do espelho são ~950 de uma vez — tomaria todas as vagas
  'tiflux_closed',
] as const satisfies readonly TicketEventType[];

export const TicketEventSchema = z.object({
  id: z.number().int(),
  ticketNumber: z.number().int(),
  userId: z.number().int().nullable(),
  userName: z.string().nullable(),
  type: TicketEventTypeSchema,
  payload: z.record(z.string(), z.unknown()),
  createdAt: Dateish,
});
export type TicketEvent = z.infer<typeof TicketEventSchema>;

/**
 * Uma mudança gravada no evento `updated` (pedido da PoolTrack, 03/10: "o
 * detalhamento em cada histórico de edição. Ex: Vanessa editou o telefone do
 * cliente"). O payload ganha `changes: TicketFieldChange[]`, UMA entrada por
 * campo que efetivamente mudou, ao lado do `fields` de sempre (que continua,
 * para quem só lê os nomes).
 *
 * `from`/`to` são o que a pessoa lê, gravados no momento da edição: o texto do
 * campo, o nome do grupo e do equipamento, o caminho do item de catálogo, o
 * texto da opção escolhida. Os ids, quando há, vão ao lado (`fromId`/`toId`).
 * Campo personalizado (`field: 'entities'`) leva o id e o rótulo do campo
 * NAQUELE momento, e o tipo — a tela formata data e caixa de seleção por ele.
 *
 * Os eventos gravados antes disto não têm `changes`: só `fields` (os nomes),
 * ou, nos de grupo/equipamento/item, só o valor novo.
 */
export interface TicketFieldChange {
  field: string;
  from: string | null;
  to: string | null;
  fromId?: number | null;
  toId?: number | null;
  /** campo personalizado: o id e o rótulo do campo quando a edição aconteceu */
  fieldId?: number;
  label?: string;
  fieldType?: string;
  /**
   * Ficha do associado (`field: 'sheet.<slot>'`, spec 031): de onde vinha o
   * valor EFETIVO antes e depois (TicketSheetSource: `description`, `title`,
   * `person`…). `from`/`to` são o efetivo, não o que a pessoa digitou: "voltar
   * ao da descrição" aparece como "→ X (da descrição)".
   */
  fromSource?: string | null;
  toSource?: string | null;
}

// ── Dashboard summaries + global event feed (v2) ────────────────────────────
export const TicketsSummaryQuerySchema = z.object({
  desk_ids: intList.optional(),
});
export type TicketsSummaryQuery = z.infer<typeof TicketsSummaryQuerySchema>;

/** GET /tickets/summary — dashboard KPI counters. `mine` = responsible == current user. */
export const TicketsSummarySchema = z.object({
  open: z.number().int(),
  mine: z.number().int(),
  unassigned: z.number().int(),
  runningTimers: z.number().int(),
  closedToday: z.number().int(),
});
export type TicketsSummary = z.infer<typeof TicketsSummarySchema>;

export const RecentEventsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(20),
});
export type RecentEventsQuery = z.infer<typeof RecentEventsQuerySchema>;

/** GET /tickets/events/recent — global feed item (event + ticket title). */
export const RecentTicketEventSchema = TicketEventSchema.extend({
  ticketTitle: z.string(),
});
export type RecentTicketEvent = z.infer<typeof RecentTicketEventSchema>;

/** GET /appointments/summary — current user's worked minutes (America/Sao_Paulo days). */
export const AppointmentsSummarySchema = z.object({
  todayMinutes: z.number().int(),
  yesterdayMinutes: z.number().int(),
  weekMinutes: z.number().int(),
});
export type AppointmentsSummary = z.infer<typeof AppointmentsSummarySchema>;

// ── Menção na comunicação interna (spec 037, PR C) ──────────────────────────
export const MentionableQuerySchema = z.object({
  q: z.string().trim().max(80).optional(),
});

/** por que a pessoa não pode ser mencionada neste ticket */
export const MENTION_BLOCK_REASONS = ['no_ticket_access', 'no_internal_access'] as const;
export type MentionBlockReason = (typeof MENTION_BLOCK_REASONS)[number];

export const MENTION_BLOCK_LABELS: Record<MentionBlockReason, string> = {
  no_ticket_access: 'não abre este ticket',
  no_internal_access: 'não vê a comunicação interna',
};

/** GET /tickets/:n/mentionable — a equipe ativa, e quem dela pode ser mencionado aqui */
export const MentionableSchema = z.object({
  items: z.array(
    z.object({
      id: z.number().int(),
      name: z.string(),
      email: z.string(),
      /** null = pode ser mencionada; senão, o motivo (o autor vê antes de enviar) */
      blocked: z.enum(MENTION_BLOCK_REASONS).nullable(),
    })
  ),
  hasMore: z.boolean(),
});
export type Mentionable = z.infer<typeof MentionableSchema>;
